import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Button } from './Button';
import { SpinnerIcon } from './Icons';
import { Label } from './Label';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from './Modal';
import { Textarea } from './Textarea';

export type CommentPromptActionKind = 'approve' | 'reject' | 'return' | 'submit';

interface CommentPromptDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with trimmed comments (empty string if none). Cancel never calls this. */
  onConfirm: (comments: string) => void;
  actionKind?: CommentPromptActionKind;
  title?: string;
  message?: string;
  placeholder?: string;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  commentsOptional?: boolean;
  zIndex?: number;
}

const defaultTitle = (kind: CommentPromptActionKind) => {
  if (kind === 'approve') return 'Approve';
  if (kind === 'return') return 'Return';
  if (kind === 'submit') return 'Submit for approval';
  return 'Reject';
};

const defaultMessage = (kind: CommentPromptActionKind) => {
  if (kind === 'approve') return 'Add an optional comment before approving.';
  if (kind === 'return') return 'Add an optional comment before returning this request to draft.';
  if (kind === 'submit') return 'Add an optional comment for the reviewers before submitting.';
  return 'Add an optional comment before rejecting.';
};

/** Same emerald / red as Approve / Reject / Return on the approval flow; sky for submit. */
const actionTone = (kind: CommentPromptActionKind) => {
  if (kind === 'approve') {
    return {
      caption: 'text-emerald-700 dark:text-emerald-300',
      bar: 'bg-emerald-600',
      ok: 'bg-emerald-600 hover:bg-emerald-700',
    };
  }
  if (kind === 'submit') {
    return {
      caption: 'text-sky-700 dark:text-sky-300',
      bar: 'bg-sky-600',
      ok: 'bg-sky-600 hover:bg-sky-700',
    };
  }
  return {
    caption: 'text-red-700 dark:text-red-300',
    bar: 'bg-red-600',
    ok: 'bg-red-600 hover:bg-red-700',
  };
};

const CommentPromptDialog: React.FC<CommentPromptDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  actionKind = 'approve',
  title,
  message,
  placeholder = 'Enter your comments…',
  confirmText = 'OK',
  cancelText = 'Cancel',
  isLoading = false,
  commentsOptional = true,
  zIndex,
}) => {
  const { colors } = useTheme();
  const [comments, setComments] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const tone = actionTone(actionKind);

  useEffect(() => {
    if (!isOpen) return;
    setComments('');
    const t = window.setTimeout(() => textareaRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  if (!isOpen) return null;

  const trimmed = comments.trim();
  const canConfirm = commentsOptional || trimmed.length > 0;

  return (
    <Modal isOpen={isOpen} onClose={() => { if (!isLoading) onClose(); }} size="lg" zIndex={zIndex}>
      <ModalHeader>
        <ModalTitle>
          <div className="flex items-center gap-3 text-left">
            <span className={cn('h-8 w-1.5 shrink-0 rounded-full', tone.bar)} aria-hidden />
            <span className={cn('font-semibold', tone.caption)}>
              {title ?? defaultTitle(actionKind)}
            </span>
          </div>
        </ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className="space-y-3 text-left">
          <p className={cn('text-sm leading-relaxed', colors.textSecondary)}>
            {message ?? defaultMessage(actionKind)}
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="approval-action-comments">
              Comments{commentsOptional ? ' (optional)' : ''}
            </Label>
            <Textarea
              ref={textareaRef}
              id="approval-action-comments"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder={placeholder}
              rows={8}
              disabled={isLoading}
              className="min-h-[180px]"
            />
          </div>
        </div>
      </ModalContent>
      <ModalFooter>
        <div className="flex w-full items-center justify-end gap-2.5">
          <Button type="button" onClick={onClose} variant="outline" disabled={isLoading}>
            {cancelText}
          </Button>
          <button
            type="button"
            onClick={() => onConfirm(trimmed)}
            disabled={isLoading || !canConfirm}
            className={cn(
              'inline-flex h-10 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white shadow-sm disabled:pointer-events-none disabled:opacity-50',
              tone.ok,
            )}
          >
            {isLoading ? <SpinnerIcon className="h-4 w-4" /> : null}
            {confirmText}
          </button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default CommentPromptDialog;
